/**
 * SQL tag for syntax highlighting in VSCode. Used by various VSCode plugins
 * such as vscode-sql-lit
 * @param strings
 */
export function sql(strings: TemplateStringsArray, ...insertions: string[]): string {
  const arr: string[] = []
  let i = 0
  for (const s of strings) {
    arr.push(s)
    if (i < insertions.length) arr.push(insertions[i++])
  }
  const result = arr.join('')
  return result
}

/**
 * Read only transaction
 */
export interface ROTransaction {
  close(): Promise<void>
}

/**
 * ReadWrite transaction
 */
export interface RWTransaction extends ROTransaction {
  /**
   * Commit changes
   */
  commit(): Promise<void>
  /**
   * Rollback changes
   */
  rollback(): Promise<void>
  /**
   * Handle transaction errors
   */
  onError?: (reason: unknown) => void
}

/**
 * Abstract connection to database
 */
export interface DBConnection {
  name: string
  /**
   * Start ReadOnly transaction
   */
  startROTransaction(): Promise<ROTransaction>

  /**
   * Start ReadWrite transaction
   */
  startRWTransaction(): Promise<RWTransaction>

  /**
   * Closes connection
   */
  close(): Promise<void>
}

/**
 * Run block of async code with ReadOnly transaction
 */
export async function runWithROTransaction<R>(db: DBConnection, block: (tx: ROTransaction) => Promise<R>): Promise<R> {
  const tx = await db.startROTransaction()
  try {
    return await block(tx)
  } finally {
    await tx.close()
  }
}

export async function runWithRWTransaction<R>(db: DBConnection, block: (tx: RWTransaction) => Promise<R>): Promise<R> {
  const tx = await db.startRWTransaction()
  try {
    const result = await block(tx)
    await tx.commit()
    return result
  } catch (e) {
    await tx.rollback()
    throw e
  } finally {
    await tx.close()
  }
}
